import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)

interface Point {
	x: number
	y: number
}

const OVERLAY_W = 224
const OVERLAY_H = 222
const DOT_RADIUS = 4.72629
const SVG_NS = 'http://www.w3.org/2000/svg'

function resolveTransform(cx: number, cy: number, transform: string | null): Point {
	if (!transform) return { x: cx, y: cy }

	const matrixMatch = transform.match(/matrix\(\s*([^)]+)\s*\)/)
	if (matrixMatch) {
		const [a, b, c, d, e, f] = matrixMatch[1].split(/[\s,]+/).map(Number)
		return {
			x: a * cx + c * cy + e,
			y: b * cx + d * cy + f,
		}
	}

	const rotateMatch = transform.match(/rotate\(\s*([^)]+)\s*\)/)
	if (rotateMatch) {
		const parts = rotateMatch[1].split(/[\s,]+/).map(Number)
		const angle = (parts[0] * Math.PI) / 180
		const ox = parts[1] ?? 0
		const oy = parts[2] ?? 0
		const dx = cx - ox
		const dy = cy - oy
		return {
			x: ox + dx * Math.cos(angle) - dy * Math.sin(angle),
			y: oy + dx * Math.sin(angle) + dy * Math.cos(angle),
		}
	}

	return { x: cx, y: cy }
}

function getSvgDimensions(svg: SVGSVGElement): { w: number; h: number } {
	const vb = svg.getAttribute('viewBox') || svg.getAttribute('viewbox')
	if (vb) {
		const parts = vb.trim().split(/[\s,]+/).map(Number)
		return { w: parts[2], h: parts[3] }
	}
	return {
		w: parseFloat(svg.getAttribute('width') || '0'),
		h: parseFloat(svg.getAttribute('height') || '0'),
	}
}

function extractDotPositions(svgEl: SVGSVGElement): Point[] {
	const { w, h } = getSvgDimensions(svgEl)
	if (w === 0 || h === 0) return []

	const offsetX = (OVERLAY_W - w) / 2
	const offsetY = (OVERLAY_H - h) / 2
	const circles = svgEl.querySelectorAll('circle')
	const positions: Point[] = []

	for (const circle of circles) {
		const cx = parseFloat(circle.getAttribute('cx') || '0')
		const cy = parseFloat(circle.getAttribute('cy') || '0')
		const resolved = resolveTransform(cx, cy, circle.getAttribute('transform'))
		positions.push({
			x: resolved.x + offsetX,
			y: resolved.y + offsetY,
		})
	}

	return positions
}

window.addEventListener('DOMContentLoaded', () => {
	const hero = document.getElementById('why-pvolve-hero')
	if (!hero) return

	const slider = hero.querySelector('.why-pvolve-slider')
	const overlayContainer = hero.querySelector('.why-pvolve-dots-overlay')
	if (!slider || !overlayContainer) return

	const headerHeight = document.querySelector('header.header')?.clientHeight || 0
	const slides = slider.querySelectorAll<HTMLElement>('.slide')
	if (slides.length === 0) return

	const numSlides = slides.length

	const allPositions: Point[][] = []
	for (const slide of slides) {
		const svg = slide.querySelector('.slide-svg-source svg') as SVGSVGElement | null
		allPositions.push(svg ? extractDotPositions(svg) : [])
	}

	const dotCount = allPositions[0]?.length || 0
	if (dotCount === 0) return

	const overlaySvg = document.createElementNS(SVG_NS, 'svg')
	overlaySvg.setAttribute('viewBox', `0 0 ${OVERLAY_W} ${OVERLAY_H}`)
	overlaySvg.setAttribute('fill', 'none')

	const dots: SVGCircleElement[] = []
	for (let i = 0; i < dotCount; i++) {
		const circle = document.createElementNS(SVG_NS, 'circle')
		const pos = allPositions[0][i]
		circle.setAttribute('cx', String(pos.x))
		circle.setAttribute('cy', String(pos.y))
		circle.setAttribute('r', String(DOT_RADIUS))
		circle.setAttribute('fill', '#FFFEFE')
		circle.setAttribute('stroke', 'white')
		circle.setAttribute('stroke-width', '2')
		dots.push(circle)
		overlaySvg.appendChild(circle)
	}

	overlayContainer.appendChild(overlaySvg)

	const ease = gsap.parseEase('power2.inOut')
	const numTransitions = numSlides - 1

	ScrollTrigger.create({
		trigger: '#why-pvolve-hero',
		pin: true,
		anticipatePin: 1,
		start: () => `top ${headerHeight}px`,
		end: () => `+=${window.innerHeight * numSlides}`,
		onUpdate(self) {
			const progress = self.progress
			const scaled = progress * numTransitions
			const segIndex = Math.min(Math.floor(scaled), numTransitions - 1)
			const segProgress = Math.min(scaled - segIndex, 1)

			const fromIndex = segIndex
			const toIndex = segIndex + 1

			const morphStart = 0.1
			const morphEnd = 0.9
			const rawMorph = Math.max(0, Math.min(1, (segProgress - morphStart) / (morphEnd - morphStart)))
			const morphProgress = ease(rawMorph)

			const fromPositions = allPositions[fromIndex]
			const toPositions = allPositions[toIndex]
			if (fromPositions?.length && toPositions?.length) {
				for (let i = 0; i < dotCount; i++) {
					const from = fromPositions[i]
					const to = toPositions[i]
					if (from && to) {
						dots[i].setAttribute('cx', String(from.x + (to.x - from.x) * morphProgress))
						dots[i].setAttribute('cy', String(from.y + (to.y - from.y) * morphProgress))
					}
				}
			}

			const fadeStart = 0.2
			const fadeEnd = 0.85
			const rawFade = Math.max(0, Math.min(1, (segProgress - fadeStart) / (fadeEnd - fadeStart)))
			const fadeProgress = ease(rawFade)

			for (let i = 0; i < numSlides; i++) {
				const s = slides[i].style
				if (i === fromIndex) {
					s.zIndex = '2'
					s.opacity = String(1 - fadeProgress)
					s.visibility = 'visible'
				} else if (i === toIndex) {
					s.zIndex = '1'
					s.opacity = '1'
					s.visibility = 'visible'
				} else {
					s.zIndex = '0'
					s.opacity = '0'
					s.visibility = 'hidden'
				}
			}
		},
	})
})
